/**
 * useSession — React hook exposing the active `SessionPort` adapter.
 *
 * Resolves the adapter from `PortsContext` (composition-root aggregate
 * introduced in T015) and subscribes to the adapter's state stream via
 * `useSyncExternalStore` so React schedules a re-render on every
 * notification.
 *
 * Return shape: the `SessionPort` reference itself. Consumers read
 * `.state` for the current `AuthState` snapshot and call
 * `.signOut()` / `.completeOnboarding()` for actions. This mirrors the port
 * interface 1:1 — no wrapper object, no renamed methods, no shadow
 * state held in React component memory. Rationale:
 *
 * - Keeps the consumer-visible surface identical to the replacement
 *   target (the queued identity-and-access feature's real adapter exposes the same `SessionPort`).
 * - Avoids re-creating a wrapper object on every render, which would
 *   break referential stability for downstream effect dependencies.
 * - Matches `contracts/ports-provider.md` §Shape: "Consumers access
 *   ports via per-port hooks" typed `function useSession(): SessionPort`.
 *
 * Throws a clear `Error` when used outside a `PortsProvider` (or when the
 * provider was mounted but no `session` port was supplied). Matches
 * `contracts/ports-provider.md` §Shape's "missing port" contract.
 */

import { useCallback, useContext, useSyncExternalStore } from 'react';

import { PortsContext } from '../providers/PortsContext.js';
import type { AuthState, SessionPort } from './SessionPort.js';

export function useSession(): SessionPort {
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useSession must be used within a PortsProvider that provides the session port',
    );
  }

  const port = ports.session;

  // `useSyncExternalStore` re-renders the calling component whenever the
  // adapter's `subscribe` callback fires. Passing `port.state` as the
  // `getSnapshot` return means React's bailout compares identity across
  // renders; `DemoSessionProvider.#transitionTo` allocates a new state
  // object on every transition, so identity changes when and only when
  // the state did.
  //
  // `subscribe` and `getSnapshot` are wrapped in `useCallback` keyed on
  // the `port` identity so their function references are stable across
  // renders. `useSyncExternalStore` uses reference equality on these
  // callbacks to decide whether to tear down and re-establish the
  // subscription; an inline arrow would force a fresh unsubscribe/
  // resubscribe on every render, which is both wasteful and a subtle
  // correctness hazard once the queued identity-and-access feature's async Entra adapter lands.
  //
  // SSR snapshot path is the same as the client snapshot path — demo
  // adapter has no server-rendered variant — so the third argument can be
  // omitted safely. Hydration-mismatch risk is nil: the initial anonymous
  // state is deterministic and matches any SSR snapshot the platform may
  // introduce later.
  // react-compiler-exception: useSyncExternalStore subscribe identity gates teardown/resubscribe
  const subscribe = useCallback((listener: () => void) => port.subscribe(listener), [port]);
  // react-compiler-exception: useSyncExternalStore getSnapshot identity must be stable per port
  const getSnapshot = useCallback(() => port.state, [port]);

  useSyncExternalStore<AuthState>(subscribe, getSnapshot);

  return port;
}
